/**
 * Self-presence hook. Read my current status from the store; mutate it
 * with optimistic updates that roll back on REST failure.
 *
 * Live updates flow through the same `usePresenceSubscription` (owner
 * subscribes to its own `user:<id>` topic), so a successful write is
 * eventually re-confirmed by the socket. That channel carries the manual
 * status and custom message only while a tab is live to publish them: it
 * says nothing on a fresh load, so a reload showed an empty row until the
 * owner's own next write. `presenceApi.me()` fills that gap: fetched once
 * per signed-in id, below.
 */

import { useCallback, useEffect } from 'react'
import { useAuthStore } from '@/features/auth/store/auth.store'
import { presenceApi, PresenceApiError } from '../services/presence.api'
import {
  type PresenceInfo,
  type PresenceStatus,
  usePresenceStore,
} from '../store/presence.store'

const MANUAL_STATUSES: PresenceStatus[] = ['online', 'away', 'busy', 'dnd']

export interface UseSelfPresence {
  me:           PresenceInfo | null
  setStatus:    (status: PresenceStatus | null) => Promise<void>
  clearStatus:  () => Promise<void>
  /** `expiresAt` (ISO 8601, future) is when the message clears itself —
   *  "Clear After". Omitted or null = never. Ignored when clearing. */
  setCustomMessage: (msg: string | null, expiresAt?: string | null) => Promise<void>
}

// One `GET /presence/me` per signed-in id, however many components mount
// this hook (settings tab, status picker, ...). A later id (switched user,
// or resolved after starting null) gets its own fetch.
const selfSnapshotFetchedFor = new Set<string>()

// Bumped whenever a local write (optimistic patch from setStatus /
// setCustomMessage) lands for an id. The bootstrap fetch below checks this
// before applying its result, so a snapshot that was already stale by the
// time the request landed can't clobber a change the user just made.
const selfWriteGeneration = new Map<string, number>()
function bumpSelfWriteGeneration(id: string): void {
  selfWriteGeneration.set(id, (selfWriteGeneration.get(id) ?? 0) + 1)
}

/**
 * Pure merge for the self-snapshot bootstrap: fills in what the local row is
 * missing, never overwrites a value that is already set locally.
 *
 * `null` (no row on the server yet) leaves the current row untouched. With no
 * local row at all, the snapshot is adopted outright.
 */
export function mergeSelfSnapshot(
  current: PresenceInfo | undefined,
  snapshot: PresenceInfo | null,
): Partial<PresenceInfo> | null {
  if (!snapshot) return null
  if (!current) return snapshot

  const patch: Partial<PresenceInfo> = {}
  if (current.manualStatus == null && snapshot.manualStatus != null) {
    patch.manualStatus = snapshot.manualStatus
  }
  if (current.customMessage == null && snapshot.customMessage != null) {
    patch.customMessage = snapshot.customMessage
    // Its expiry comes with it, so the pair stays one message's.
    if (snapshot.customMessageExpiresAt != null) {
      patch.customMessageExpiresAt = snapshot.customMessageExpiresAt
    }
  }
  return Object.keys(patch).length > 0 ? patch : null
}

export function useSelfPresence(): UseSelfPresence {
  // My own presence row is keyed by the per-org contactPresenceId (the topic I
  // subscribe to), NOT the auth user id. `presenceApi.setMe` derives identity
  // from the token server-side, so it stays argument-free.
  const myId = useAuthStore((s) => s.presenceId)
  const meBy = usePresenceStore((s) => (myId ? s.byId[myId] : null))
  const patchOne = usePresenceStore((s) => s.patchOne)

  const setStatus = useCallback(
    async (status: PresenceStatus | null): Promise<void> => {
      if (!myId) return
      // Validate enum + null up-front; reject silently rather than crash.
      if (status !== null && !MANUAL_STATUSES.includes(status)) return

      const previous = usePresenceStore.getState().byId[myId]
      // Optimistic: patch immediately so the UI feels responsive.
      bumpSelfWriteGeneration(myId)
      patchOne(myId, {
        manualStatus: status,
        status: status ?? (previous?.inMeeting ? 'busy' : 'online'),
      })
      try {
        const { presence } = await presenceApi.setMe({ status })
        bumpSelfWriteGeneration(myId)
        usePresenceStore.getState().setOne(presence)
      } catch (err) {
        // Roll back on failure.
        bumpSelfWriteGeneration(myId)
        if (previous) patchOne(myId, previous)
        if (!(err instanceof PresenceApiError)) throw err
        throw err
      }
    },
    [myId, patchOne],
  )

  const clearStatus = useCallback(() => setStatus(null), [setStatus])

  const setCustomMessage = useCallback(
    async (msg: string | null, expiresAt: string | null = null): Promise<void> => {
      // Thrown, not returned. These used to return quietly, and the caller —
      // which cannot tell "saved" from "never sent" — told the user it was
      // updated while nothing had been stored anywhere.
      if (!myId) throw new Error('presence identity not resolved yet')
      if (typeof msg === 'string' && msg.length > 80) throw new Error('status message exceeds 80 characters')

      // A cleared message has no expiry. Sent only when there is one, so a
      // message with no "Clear After" goes out exactly as it always has.
      const expiry = msg ? expiresAt : null
      const previous = usePresenceStore.getState().byId[myId]
      const previousMessage = previous?.customMessage ?? null
      const previousExpiry = previous?.customMessageExpiresAt ?? null
      bumpSelfWriteGeneration(myId)
      patchOne(myId, { customMessage: msg, customMessageExpiresAt: expiry })
      try {
        const { presence } = await presenceApi.setMe(
          expiry ? { customMessage: msg, customMessageExpiresAt: expiry } : { customMessage: msg },
        )
        bumpSelfWriteGeneration(myId)
        usePresenceStore.getState().setOne(presence)
      } catch (err) {
        bumpSelfWriteGeneration(myId)
        // Roll back even when there was no row to restore. `patchOne` creates
        // one from a placeholder, so skipping the rollback whenever `previous`
        // was missing left the unsaved message on screen, shown as set, gone
        // after the next reload.
        patchOne(myId, { customMessage: previousMessage, customMessageExpiresAt: previousExpiry })
        if (!(err instanceof PresenceApiError)) throw err
        throw err
      }
    },
    [myId, patchOne],
  )

  // Bootstrap: the live socket channel carries manual status / custom message
  // only from a tab that is currently publishing them, so a fresh page load
  // has nothing there until this account's own next write. Fetch the stored
  // snapshot once per id and fill in whatever the local row is missing.
  useEffect(() => {
    if (!myId) return
    if (selfSnapshotFetchedFor.has(myId)) return
    selfSnapshotFetchedFor.add(myId)

    const id = myId
    const generationAtStart = selfWriteGeneration.get(id) ?? 0

    presenceApi
      .me()
      .then(({ presence }) => {
        // A local write landed while this was in flight: the store already
        // holds something newer than this response, leave it alone.
        if ((selfWriteGeneration.get(id) ?? 0) !== generationAtStart) return

        const current = usePresenceStore.getState().byId[id]
        const patch = mergeSelfSnapshot(current, presence)
        if (!patch) return

        if (current) {
          patchOne(id, patch)
        } else {
          // No local row yet: adopt the snapshot, keyed by the id this hook
          // already uses everywhere else (not whatever the response itself
          // carries as `userId`).
          usePresenceStore.getState().setOne({ ...(patch as PresenceInfo), userId: id })
        }
      })
      .catch((err) => {
        // Guests and signed-out tabs throw a PresenceApiError here routinely,
        // not worth logging. Anything else is unexpected.
        if (!(err instanceof PresenceApiError)) {
          console.warn('[presence] self snapshot fetch failed', err)
        }
      })
  }, [myId, patchOne])

  return { me: meBy ?? null, setStatus, clearStatus, setCustomMessage }
}
